import {
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isSameWeek,
  startOfWeek,
} from "date-fns";
import { parseFromTimeZone } from "date-fns-timezone";
import { utcToZonedTime } from "date-fns-tz";

export const convertTZ = (date, tzString) => {
  return new Date(
    (typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", {
      timeZone: tzString,
    })
  );
};

export const convertToTimezone = (date, timeZone) => {
  return parseFromTimeZone(date, { timeZone });
};

export const formattedDate = (date) => {
  const parseDate = date instanceof Date ? date : new Date(date);
  const d = new Date(parseDate);
  const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(d);
  const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(d);
  const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(d);
  return `${mo}/${da}/${ye}`;
};

export const hourDateFormat = (date) => {
  const parseDate = date instanceof Date ? date : new Date(date);
  const d = parseDate;
  const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(d);
  const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(d);
  const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(d);
  const options = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  };
  const hour = new Intl.DateTimeFormat("en", options).format(d);
  return `${mo}/${da}/${ye} ${hour}`;
};

export const hourDateEventFormat = (date) => {
  // Force utc format
  const parseDate = date instanceof Date ? date : new Date(`${date}Z`);
  const d = parseDate;
  const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(d);
  const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(d);
  const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(d);
  const options = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
  };
  const hour = new Intl.DateTimeFormat("en", options).format(d);
  return `${mo}/${da}/${ye} ${hour}`;
};

export const formattedDateBackend = (date) => {
  const parseDate = date instanceof Date ? date : new Date(date);
  const d = new Date(parseDate);
  const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(d);
  const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(d);
  const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(d);
  return `${ye}-${mo}-${da}`;
};

export const formattedToDateTimeBackend = (date) => {
  const parseDate = date instanceof Date ? date : new Date(date);
  const d = new Date(parseDate);
  const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(d);
  const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(d);
  const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(d);
  const hour = new Intl.DateTimeFormat("en", {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle: "h23",
  }).format(d);
  return `${ye}-${mo}-${da}T${hour}Z`;
};

export const parseDateFormat = (d) => {
  let cloneDate = d;
  if (cloneDate instanceof Date !== true) {
    cloneDate = new Date(`${d}Z`);
  }
  return cloneDate;
};

export const formatUtcToTimezone = (date, timeZone = null) => {
  const parseDate = parseDateFormat(date);
  let tmz = timeZone;
  if (timeZone === null) {
    tmz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  const d = utcToZonedTime(parseDate, tmz);
  return d;
};

/** @arg date {string | Date} String or Date to cast as UTC date. */
export const formatUtcToDate = (date) => {
  const parseDate =
    date instanceof Date ? date : new Date(`${date}Z`.replace("ZZ", "Z"));
  return parseDate;
};

export const isValidDate = (date) =>
  date !== null && date instanceof Date && !Number.isNaN(date.getTime());

export const parseDateTo = (startDate, endDate) => {
  const currentFormat = "EEEE, MMM dd, yyyy h:mm a";

  if (startDate === null) {
    return format(new Date(), currentFormat);
  }

  if (isSameDay(startDate, endDate)) {
    return `${format(startDate, currentFormat)} - ${format(endDate, "h:mm a")}`;
  }

  return `${format(startDate, currentFormat)} - ${format(
    endDate,
    currentFormat
  )}`;
};

export const dateToTextRead = (date) => {
  const currentFormat = "EEEE, MMM dd, yyyy h:mm a";
  if (date instanceof Date !== true) {
    return "";
  }
  return format(date, currentFormat);
};

export const getDayNameOfTheWeek = (date = new Date()) => {
  return format(date, "EEEEEE");
};

export const daysOfWeek = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export const getStartOfWeek = ({ date = new Date(), options }) => {
  return startOfWeek(date, options);
};

export const getEndOfWeek = ({ date = new Date(), options }) => {
  return endOfWeek(date, options);
};

export const getIsSameWeek = ({ date = new Date(), options }) => {
  return isSameWeek(date, options);
};

export const getEachDayOfInterval = ({ start, end }) => {
  return eachDayOfInterval({ start, end });
};
